import React, { useEffect } from 'react';
import { StyledProfessions, StyledProfessionsContent } from './styled';
import AppBar from '../../../../components/AppBar';
import Layout from '../../../../components/Layout';
import TabsLinks from '../../../../components/TabsLinks';
import { paths } from '../../../../routes';
import Button from '../../../../components/Button';
import Modal from '../../../../components/Modal';
import { useState } from 'react';
import Icon from '../../../../components/Icon';
import FormProfession from '../../../../forms/FormProfession';
import { ApiProfessions } from '../../../../api/professions';
import { useMemo } from 'react';
import List from '../../../../components/List';
import ListItem from '../../../../components/ListItem';
import { hex2rgba, makeStyles } from '../../../../utils/styles';
import Typography from '../../../../components/Typography';
import { Col, Row } from 'react-styled-flexboxgrid';
import Paper from '../../../../components/Paper';
import Divider from '../../../../components/Divider';
import FormLabel from '../../../../components/FormLabel';
import Input from '../../../../components/Input';
import useTheme from '../../../../hooks/useTheme';
import { ApiSkills } from '../../../../api/skills';
import Loader from '../../../../components/Loader';
import Tags from '../../../../components/Tags';
import ScrollArea from '../../../../components/ScrollArea';
import ButtonGroup from '../../../../components/ButtonGroup';
import Box from '../../../../components/Box';
import { filterDataByString, findByID, parseProffesion } from '../../../../utils/helpers';
import Search from '../../../../components/Search';
import FormConfirmDelete from '../../../../forms/FormConfirmDelete';
import { useApi } from '../../../../hooks/useApi';
import { OPERATIONS } from '../../../../utils/constans';
import RowWrapper from '../../../../components/RowWrapper';
import { useManage } from '../../../../hooks/useManage';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(css => ({
    boxSearch: css`
        padding: 20px 20px 20px 20px;
    `,
    paperList: css`
        padding: 0;
        padding-bottom: 20px;
        height: 100%;
    `,
    paperActive: css`
        display: flex;
        flex-direction: column;
        padding: 25px 30px 15px 30px;
        height: 100%;
    `,
    boxActive: css`
        margin-top: 26px;
    `,
    list: css`
    `,
    listItem: css`
        position: relative;
        padding: 16px 20px;
        cursor: pointer;
        transition: all 0.3s;
        ${({ active }) => active && css`
            background-color: ${({ theme }) => theme.colors.lightCyan};
        `}
        &:hover {
            ${({ active }) => !active && css`
                background-color: ${({ theme }) => hex2rgba(theme.colors.lightCyan, 0.3)};
            `}
        }
        &:after {
            content: '';
            position: absolute;
            left: 20px;
            right: 20px;
            bottom: 0;
            height: 1px;
            background-color: ${({ theme }) => theme.colors.mediumGrey};
        }
        &:last-child {
            &:after {
                content: none;
            }
        }
    `,
    skillsTitle: css`
        margin-top: 30px;
        margin-bottom: 30px;
    `,
    noMarginBottom: css`
        margin-bottom: 0;
    `,
    divider: css`
        margin-top: 26px;
    `,
    dividerBottom: css`
        margin-bottom: 15px;
    `,
    boxBottom: css`
        margin-top: auto;
    `
}));

const Professions = () => {
    const { t } = useTranslation()
    const theme = useTheme();
    const styles = useStyles();
    const [{ operation, id: activeID }, setState] = useManage();
    const [searchText, setSearchValue] = useState("");

    // Setup request
    const [{ data: skills, loading: skillsLoading }] = useApi(ApiSkills.get);
    const [{ data: professions, loading: professionsLoading }, getProfessions] = useApi(ApiProfessions.get);
    const [{ loading: createProfessionLoading }, addProfession] = useApi(ApiProfessions.create, { manual: true });
    const [{ loading: updateProfessionLoading }, updateProfession] = useApi(ApiProfessions.update, { manual: true });
    const [{ loading: deleteProfessionLoading }, deleteProfession] = useApi(ApiProfessions.delete, { manual: true });

    // Memoize data
    const active = useMemo(
        () => professions && parseProffesion(findByID(professions, activeID)),
        [professions, activeID]
    );

    const professionsFiltered = useMemo(
        () => {
            return filterDataByString(professions, searchText, profession => profession?.name);
        },
        [professions, searchText]
    );

    useEffect(() => {
        if (professions && professions.length > 0 && activeID === null) {
            setState(OPERATIONS.READ, professions[0]?.id);
        }
    }, [professions, activeID, setState]);

    const handleClickListItem = id => () => setState(OPERATIONS.READ, id);

    const handleChangeSearch = e => setSearchValue(e.target.value);

    const handleClickAdd = () => setState(OPERATIONS.ADD);

    const handleClickEdit = () => setState(OPERATIONS.EDIT);

    const handleClickDelete = () => setState(OPERATIONS.DELETE);

    const handleClickDuplicate = () => setState(OPERATIONS.DUPLICATE);

    const handleClickCancelModal = () => setState(OPERATIONS.READ);

    const handleSubmit = async data => {
        if (operation === OPERATIONS.EDIT) {
            //UPDATE
            const res = await updateProfession(activeID, data);
            if (res?.data) {
                toast.success(t('Zawód został zaktualizowany'));
                setState(OPERATIONS.READ);
                getProfessions();
            }
        } else {
            //CREATE
            const res = await addProfession(data);
            if (res?.data) {
                toast.success(t('Zawód został dodany'));
                const { id = null } = res.data;
                setState(OPERATIONS.READ, id);
                getProfessions();
            }
        }
    };

    const handleSubmitDelete = async () => {
        //DELETE
        const res = await deleteProfession(activeID);
        if (res?.data) {
            toast.success(t('Zawód został usunięty'));
            setState(OPERATIONS.READ, null);
            getProfessions();
        }
    }

    return (
        <StyledProfessions>
            <AppBar>
                <Typography
                    font={theme.fonts.mulishRegular}
                    as="h2"
                >
                    {t("Panel administracyjny")}
                </Typography>
            </AppBar>
            <TabsLinks
                tabs={[{
                    to: paths.professions,
                    name: t('Zawody')
                }]}
                rightContent={
                    <Button
                        startIcon={
                            <Icon
                                name="add"
                                width={12}
                                height={12}
                            />
                        }
                        onClick={handleClickAdd}
                    >
                        {t("Dodaj zawód").toUpperCase()}
                    </Button>
                }
            />
            <Layout>
                <StyledProfessionsContent>
                    <RowWrapper>
                        <Row bottom="xs">
                            <Col xs={4}>
                                {
                                    professions &&
                                    <Paper
                                        customStyle={styles.paperList}
                                    >
                                        <Box customStyle={styles.boxSearch}>
                                            <Search
                                                value={searchText}
                                                onChange={handleChangeSearch}
                                                fullWidth
                                            />
                                        </Box>
                                        <ScrollArea
                                            heightModifiers={{
                                                isCalc: true,
                                                offset: 60
                                            }}
                                        >
                                            <List
                                                customStyle={styles.list}
                                                noDivider
                                            >
                                                {
                                                    professionsFiltered.map(profession => (
                                                        <ListItem
                                                            key={profession.id}
                                                            customStyle={styles.listItem}
                                                            active={profession.id === activeID}
                                                            onClick={handleClickListItem(profession.id)}
                                                        >
                                                            {profession.name}
                                                        </ListItem>
                                                    ))
                                                }
                                            </List>
                                        </ScrollArea>
                                    </Paper>
                                }
                            </Col>
                            <Col xs={8}>
                                {
                                    active &&
                                    <Paper
                                        customStyle={styles.paperActive}
                                    >
                                        <Typography customStyle={styles.title} as="h1" font={theme.fonts.comfortaaLight}>
                                            {active?.name}
                                        </Typography>
                                        <Divider customStyle={styles.divider} />
                                        <ScrollArea
                                            heightModifiers={{
                                                isCalc: true,
                                                offset: 115
                                            }}
                                        >
                                            <Box customStyle={styles.boxActive}>
                                                <Row between="xs">
                                                    <Col xs={5}>
                                                        <Row bottom="xs">
                                                            <Col xs={12}>
                                                                <FormLabel>
                                                                    {t("Stawka godzinowa dla kandydata")}
                                                            </FormLabel>
                                                            </Col>
                                                            <Col xs={6}>
                                                                <Input
                                                                    type="number"
                                                                    value={active?.hourly_rate_user}
                                                                    customStyle={styles.noMarginBottom}
                                                                    readonly
                                                                />
                                                            </Col>
                                                            <Col xs={6}>
                                                                <Input
                                                                    value="brutto"
                                                                    readonly
                                                                    customStyle={styles.noMarginBottom}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                    <Col xs={5}>
                                                        <Row bottom="xs">
                                                            <Col xs={12}>
                                                                <FormLabel>
                                                                    {t("Stawka godzinowa dla obiektu")}
                                                            </FormLabel>
                                                            </Col>
                                                            <Col xs={6}>
                                                                <Input
                                                                    type="number"
                                                                    value={active?.hourly_rate_admin}
                                                                    customStyle={styles.noMarginBottom}
                                                                    readonly
                                                                />
                                                            </Col>
                                                            <Col xs={6}>
                                                                <Input
                                                                    value="brutto"
                                                                    readonly
                                                                    customStyle={styles.noMarginBottom}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                                <Typography font={theme.fonts.mulishBold} as="h2" customStyle={styles.skillsTitle}>
                                                    {t("Umiejętności")}
                                                </Typography>
                                                <Tags
                                                    tags={active?.skills}
                                                    readonly
                                                />
                                            </Box>
                                        </ScrollArea>
                                        <Box
                                            customStyle={styles.boxBottom}
                                        >
                                            <Divider customStyle={styles.dividerBottom} />
                                            <Row between="xs">
                                                <Col>
                                                    <Button
                                                        variant="text"
                                                        startIcon={
                                                            <Icon name="delete" width={12} height={12} />
                                                        }
                                                        onClick={handleClickDelete}
                                                    >
                                                        {t("USUŃ ZAWÓD")}
                                                        </Button>
                                                </Col>
                                                <Col>
                                                    <ButtonGroup
                                                        gutters={10}
                                                    >
                                                        <Button
                                                            variant="text"
                                                            startIcon={
                                                                <Icon name="edit" width={12} height={12} />
                                                            }
                                                            onClick={handleClickEdit}
                                                        >
                                                            {t("Edytuj zawód")}
                                                            </Button>
                                                        <Button
                                                            variant="text"
                                                            startIcon={
                                                                <Icon name="duplicate" width={12} height={12} />
                                                            }
                                                            onClick={handleClickDuplicate}
                                                        >
                                                            {t("Duplikuj zawód")}
                                                            </Button>
                                                    </ButtonGroup>
                                                </Col>
                                            </Row>
                                        </Box>
                                    </Paper>
                                }
                            </Col>
                        </Row>
                    </RowWrapper>
                </StyledProfessionsContent>
            </Layout>
            <Modal
                open={operation === OPERATIONS.ADD || operation === OPERATIONS.EDIT || operation === OPERATIONS.DUPLICATE}
            >
                <FormProfession
                    loading={skillsLoading || createProfessionLoading || updateProfessionLoading}
                    edit={operation === OPERATIONS.EDIT}
                    initialValues={operation === OPERATIONS.EDIT || operation === OPERATIONS.DUPLICATE ? active?.form_values : {}}
                    skillsHints={skills}
                    onSubmit={handleSubmit}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Modal
                open={operation === OPERATIONS.DELETE}
            >
                <FormConfirmDelete
                    loading={deleteProfessionLoading}
                    onSubmit={handleSubmitDelete}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Loader show={skillsLoading || professionsLoading || createProfessionLoading || updateProfessionLoading || deleteProfessionLoading} />
        </StyledProfessions >
    )
}

export default Professions;
